'use client'

import _ from 'lodash'
import { useTimeout } from 'ahooks'
import { twMerge } from 'tailwind-merge'
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'
import React, { useState, useEffect } from 'react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
  DocumentCheckIcon,
} from '@heroicons/react/20/solid'
import { useGlobalStore } from '@/store/provider'

const typeStyles = {
  success:
    'bg-zinc-800 dark:bg-zinc-300 text-zinc-100 dark:text-black',
  warning:
    'bg-zinc-800 dark:bg-zinc-300 text-yellow-500 dark:text-black',
  error:
    'bg-zinc-800 dark:bg-zinc-300 text-red-500 dark:text-red-800',
  info:
    'bg-zinc-800 dark:bg-zinc-300 text-zinc-100 dark:text-black',
  sent:
    'bg-zinc-800 dark:bg-zinc-300 text-zinc-100 dark:text-black',
}


export function Toast() {
  const { toast, setToast } = useGlobalStore((state) => state)
  const [open, setOpen] = useState(true)
  const [delay, setDelay] = useState<number | undefined>(undefined)

  const clear = useTimeout(() => {
    setDelay(undefined)
    setOpen(false)
    setToast(null)
  }, delay)

  useEffect(() => {
    setOpen(!_.isEmpty(toast))
    if (toast?.type === 'success') {
      setDelay(4000)
    }
  }, [toast])

  const type = toast?.type || 'info'
  const iconStyle = 'size-10 mr-3'
  const Icon = () => {
    if (type === 'success') return <CheckCircleIcon className={iconStyle} />
    if (type === 'error') return <XCircleIcon className={iconStyle} />
    if (type === 'info') return <InformationCircleIcon className={iconStyle} />
    if (type === 'warning') return <ExclamationTriangleIcon className={iconStyle} />
    if (type === 'sent') return <DocumentCheckIcon className={iconStyle} />
  }

  if (!toast) {
    return <></>
  }

  function close() {
    clear
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={close} className='relative z-50'>
      {
        toast?.showBackdrop &&
        <DialogBackdrop transition className='dialog-backdrop' />
      }
      <div className='fixed inset-0 w-screen h-screen overflow-y-scroll'>
        <div className='flex items-center justify-center mt-10'>
          <DialogPanel
            transition
            className={twMerge(
              'relative transform rounded-md px-6 py-5',
              'w-full max-w-lg mx-4 shadow-lg',
              'transition',
              'data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200',
              'data-enter:ease-out data-leave:ease-in',
              typeStyles[type]
            )}
          >
            <div className='w-full flex items-center justify-between'>
              <div className='flex items-center'>
                <Icon />
                <div className='text-base/7 font-normal'>
                  {toast?.message}
                </div>
              </div>
              <button type='button' className='rounded-full p-1 -mr-1 hover:bg-zinc-500/50'>
                <span className='sr-only'>Dismiss</span>
                <XMarkIcon className='size-5' onClick={close} />
              </button>
            </div>
          </DialogPanel>
        </div>
      </div >
    </Dialog >
  )
}